import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

/**
 * SampleDataBadge Component
 * 
 * A visual indicator badge that shows when sample/demo data is being displayed
 * instead of actual data from the backend.
 * 
 * @param {boolean} show - Whether to show the badge
 * @param {string} position - Position of the badge ('top-right', 'top-left', etc.)
 * @param {string} size - Size of the badge ('small', 'medium')
 * @param {boolean} absolute - Whether to use absolute positioning
 * @param {object} sx - Additional styles
 */
const SampleDataBadge = ({
    show = true,
    position = 'top-right',
    size = 'small',
    absolute = true,
    sx = {}
}) => {
    if (!show) return null;

    // Position styles based on position prop
    const positionStyles = absolute ? {
        position: 'absolute',
        ...(position.includes('top') && { top: '8px' }),
        ...(position.includes('bottom') && { bottom: '8px' }),
        ...(position.includes('right') && { right: '8px' }),
        ...(position.includes('left') && { left: '8px' }),
    } : {};

    // Size styles
    const sizeStyles = {
        small: {
            padding: '2px 8px',
            fontSize: '10px',
            iconSize: '10px',
        },
        medium: {
            padding: '4px 12px',
            fontSize: '12px',
            iconSize: '12px',
        },
    }[size] || sizeStyles.small;

    return (
        <Tooltip
            title="This chart is displaying sample data for demonstration purposes. Actual data will appear when available from the system."
            arrow
            placement="top"
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    bgcolor: '#fff3e0',
                    border: '1px solid #ff9800',
                    borderRadius: '12px',
                    padding: sizeStyles.padding,
                    zIndex: 10,
                    cursor: 'help',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: '#ffe0b2',
                        transform: 'scale(1.02)',
                    },
                    ...positionStyles,
                    ...sx,
                }}
            >
                {/* Info Icon */}
                <Box
                    sx={{
                        width: sizeStyles.iconSize,
                        height: sizeStyles.iconSize,
                        borderRadius: '50%',
                        bgcolor: '#ff9800',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '8px',
                            fontWeight: 700,
                            color: '#fff',
                            lineHeight: 1,
                        }}
                    >
                        !
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: sizeStyles.fontSize,
                        fontWeight: 600,
                        color: '#e65100',
                        fontFamily: '"Inter", sans-serif',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    Sample Data
                </Typography>
            </Box>
        </Tooltip>
    );
};

/**
 * ChartWithSampleBadge Component
 * 
 * A wrapper component that adds the SampleDataBadge to any chart
 * 
 * @param {React.ReactNode} children - The chart component to wrap
 * @param {boolean} isSampleData - Whether sample data is being displayed
 * @param {object} badgeProps - Props to pass to SampleDataBadge
 */
export const ChartWithSampleBadge = ({
    children,
    isSampleData = false,
    badgeProps = {}
}) => {
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <SampleDataBadge show={isSampleData} {...badgeProps} />
            {children}
        </Box>
    );
};

export default SampleDataBadge;
